import { useState, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Typography,
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import PromoCatalogFilters from '../components/PosStorePromotions/PromoCatalogFilters'
import PromoCatalogTable from '../components/PosStorePromotions/PromoCatalogTable'
import { posStorePromotionService } from '../services'
import { downloadPosPromotionsExcel } from '../utils/posPromotionExport'
import useAuthStore from '../store/authStore'
import { canExportData } from '../utils/adminPermissions'

const PosStorePromotionsListPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const showExportExcel = canExportData(adminPermissions)
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    type: '',
    validFrom: undefined,
    validTo: undefined,
  })
  const [resetKey, setResetKey] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' })

  const handleFiltersChange = useCallback((next) => {
    setFilters(next)
    setResetKey((k) => k + 1)
  }, [])

  const stableFilters = useMemo(() => filters, [filters])

  const handleExport = useCallback(async () => {
    try {
      setExporting(true)
      const response = await posStorePromotionService.list({
        page: 1,
        limit: 0,
        search: stableFilters.search || undefined,
        status: stableFilters.status || 'all',
        type: stableFilters.type || undefined,
        validFrom: stableFilters.validFrom || undefined,
        validTo: stableFilters.validTo || undefined,
      })
      const items = Array.isArray(response.items) ? response.items : []
      const stamp = new Date().toISOString().slice(0, 10)
      downloadPosPromotionsExcel(items, `pos-promotions-${stamp}.xlsx`)
      setToast({
        open: true,
        severity: 'success',
        message: `Exported ${items.length} promotion${items.length === 1 ? '' : 's'}`,
      })
    } catch (error) {
      console.error('Error exporting POS promotions:', error)
      setToast({
        open: true,
        severity: 'error',
        message: 'Could not export promotions. Try again.',
      })
    } finally {
      setExporting(false)
    }
  }, [stableFilters])

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#1a1a1a' }}>
            List POS Promotions
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Browse the synced POS promotion catalog (read-only)
          </Typography>
        </Box>
        {showExportExcel ? (
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'center' },
              bgcolor: '#e90000',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#c40000' },
            }}
          >
            Export Excel
          </Button>
        ) : null}
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Supported Promotion Types
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          The system currently supports the following promotion types: bonus, discount, tp
          (including Fixed from Shwapno), %, and percentage.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Note: If any new promotion type is introduced or existing types are modified on the
          Shwapno end, please inform the Aspen Team so the necessary changes can be implemented.
          Unsupported promotion types will be skipped during promotion sync.
        </Typography>
      </Alert>

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent>
          <PromoCatalogFilters onFiltersChange={handleFiltersChange} />
          <PromoCatalogTable filters={stableFilters} resetKey={resetKey} />
        </CardContent>
      </Card>
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default PosStorePromotionsListPage
